import React from 'react'
import { Link, withRouter } from 'react-router-dom'
import { Row, Cell } from '../../../../components/Table'
import { getMessage } from '../../../../lib/translator'
import {
  isDspSource,
  getOrderSourceLabel,
} from '../../DeliveryOrders/constants/orderSource'
import { buildDspBackOfficeUrl } from '../../DeliveryOrders/Details/PickingOrderDetails/utils/buildDspBackOfficeUrl'
import {
  getPickingOrderStatusClassName,
  getPickingOrderStatusLabel,
} from '../constants/status'
import { getPickingOrderSourceClassName } from '../constants/source'
import {
  formatOrderSlotDate,
  formatOrderSlotTime,
  getOrderActionDate,
  getOrderActionTime,
} from '../utils/displayFormatters'
import { isAmendedOrder } from '../utils/isAmendedOrder'
import { getTableHeaders } from './columns'

const FILL_RATE_VISIBLE_STATUSES = new Set([
  'PENDING_SHIPMENT',
  'SHIPPED',
  'DELIVERED',
  'COMPLETED',
])

export const showFillRateForPickingStatus = (status) =>
  FILL_RATE_VISIBLE_STATUSES.has(status)

export const FillRateCell = ({ status, fillRate }) => {
  if (!showFillRateForPickingStatus(status) || !(fillRate === 0 || fillRate)) {
    return <React.Fragment>-</React.Fragment>
  }

  return (
    <span className={fillRate < 80 ? 'fill-rate' : 'text-muted'}>
      {`${Math.round(fillRate * 100) / 100}%`}
    </span>
  )
}

export const buildPickingOrderDetailLink = (basePath, row) => {
  const {
    referenceNumber,
    orderId,
    childOrderId,
    poaChildOrderId,
    amendments,
    source,
    preferredDate,
    slotStartTime,
    slotEndTime,
    status,
    storeId,
    sapStoreId,
    placedOn,
    orderAddress,
  } = row
  const detailKey = referenceNumber || orderId
  const pathname = `${basePath}/picking-delivery-orders/${detailKey}`

  if (isDspSource(source)) {
    return {
      pathname,
      state: {
        pickingOrderDetail: {
          referenceNumber,
          orderId,
          childOrderId,
          poaChildOrderId,
          // A main order's only POA marker: without it the detail page cannot tell the
          // order has amendments and skips the linked-orders section.
          amendments,
          source,
          preferredDate,
          slotStartTime,
          slotEndTime,
          status,
          storeId,
          sapStoreId,
          placedOn,
          orderAddress,
        },
      },
    }
  }

  return {
    pathname,
    state: {
      pickingOrderDetail: {
        referenceNumber,
        orderId,
        childOrderId,
        poaChildOrderId,
        amendments,
        source,
        preferredDate,
        slotStartTime,
        slotEndTime,
        status,
        storeId,
        sapStoreId,
        placedOn,
        orderAddress,
      },
    },
  }
}

const ChevronIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ExternalLinkIcon = () => (
  <svg
    className="picking-order-backoffice-icon"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="currentColor"
      d="M14 3h7v7h-2V6.4l-9.3 9.3-1.4-1.4L17.6 5H14V3zM5 5h6v2H7v10h10v-4h2v6H5V5z"
    />
  </svg>
)

const DateTimeCell = ({ value }) => {
  const date = getOrderActionDate(value)
  const time = getOrderActionTime(value)
  if (!date && !time) {
    return <React.Fragment>-</React.Fragment>
  }
  return (
    <React.Fragment>
      <div>{date || '-'}</div>
      {time ? <small className="text-muted">{time}</small> : null}
    </React.Fragment>
  )
}

/** Legacy-style Order Slot: date on first line, slot time range muted below. */
const OrderSlotCell = ({ preferredDate, slotStartTime, slotEndTime }) => {
  const date = formatOrderSlotDate(preferredDate)
  const slot = formatOrderSlotTime(slotStartTime, slotEndTime)
  if (!date && !slot) {
    return <React.Fragment>-</React.Fragment>
  }
  return (
    <React.Fragment>
      <div>{date || ''}</div>
      {slot ? (
        <small
          className={`text-muted ${!slotEndTime ? 'zero-font' : ''}`.trim()}
        >
          {slot}
        </small>
      ) : null}
    </React.Fragment>
  )
}

const DspReferenceCell = ({ row, referenceNumber }) => {
  const backOfficeUrl = buildDspBackOfficeUrl(row)
  const backOfficeLabel = getMessage('picking.orderDetail.dsp.openInBackOffice')

  return (
    <span className="picking-order-ref-cell">
      <span className="picking-order-ref">{referenceNumber || '-'}</span>
      {backOfficeUrl ? (
        <>
          <span className="picking-order-ref-divider" aria-hidden="true">
            |
          </span>
          <a
            className="picking-order-backoffice-btn"
            href={backOfficeUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={backOfficeLabel}
            data-testid="picking-order-backoffice-link"
            onClick={(event) => event.stopPropagation()}
          >
            <ExternalLinkIcon />
            <span className="picking-order-backoffice-tooltip" role="tooltip">
              {backOfficeLabel}
            </span>
          </a>
        </>
      ) : null}
    </span>
  )
}

const ReferenceNumberCell = ({ row, referenceNumber, linkTo }) => {
  if (isDspSource(row.source)) {
    return <DspReferenceCell row={row} referenceNumber={referenceNumber} />
  }

  return (
    <Link to={linkTo} onClick={(event) => event.stopPropagation()}>
      {referenceNumber || '-'}
    </Link>
  )
}

const PickingOrderRowComponent = ({ basePath, row, history }) => {
  const {
    referenceNumber,
    orderId,
    childOrderId,
    source,
    preferredDate,
    slotStartTime,
    slotEndTime,
    status,
    placedOn,
    fillRate,
  } = row
  const detailKey = referenceNumber || orderId
  const linkTo = buildPickingOrderDetailLink(basePath, row)

  const openDetail = () => {
    if (detailKey) {
      history.push(linkTo)
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openDetail()
    }
  }

  return (
    <Row
      className="picking-order-row"
      onClick={openDetail}
      onKeyDown={detailKey ? handleKeyDown : undefined}
      role={detailKey ? 'button' : undefined}
      tabIndex={detailKey ? 0 : undefined}
    >
      <Cell className="column-order-number">
        {detailKey ? (
          <ReferenceNumberCell
            basePath={basePath}
            row={row}
            referenceNumber={referenceNumber}
            linkTo={linkTo}
          />
        ) : (
          '-'
        )}
      </Cell>
      <Cell className="column-child-order-id">
        {childOrderId || '-'}
        {isAmendedOrder(row) ? (
          <span className="picking-order-amended-badge">
            {getMessage('picking.orderDetail.amended.badge')}
          </span>
        ) : null}
      </Cell>
      <Cell className="column-order-source">
        {source ? (
          <span className={getPickingOrderSourceClassName(source)}>
            {getOrderSourceLabel(source)}
          </span>
        ) : (
          '-'
        )}
      </Cell>
      <Cell className="column-creation-time">
        <DateTimeCell value={placedOn} />
      </Cell>
      <Cell className="column-creation-time">
        <OrderSlotCell
          preferredDate={preferredDate}
          slotStartTime={slotStartTime}
          slotEndTime={slotEndTime}
        />
      </Cell>
      <Cell className="column-order-status">
        <span className={getPickingOrderStatusClassName(status)}>
          {getPickingOrderStatusLabel(status) || status || '-'}
        </span>
      </Cell>
      <Cell className="column-fill-rate">
        <FillRateCell status={status} fillRate={fillRate} />
      </Cell>
      <Cell className="column-order-chevron">
        <span className="picking-order-chevron" aria-hidden="true">
          <ChevronIcon />
        </span>
      </Cell>
    </Row>
  )
}

export const PickingOrderRow = withRouter(PickingOrderRowComponent)

export const tabularView = (basePath = '/operations') => ({
  headers: getTableHeaders(),
  row: (row) => <PickingOrderRow basePath={basePath} row={row} />,
})

export default tabularView
